import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EventForm from './EventForm';
import {
  emptyCollection,
  emptyEvent,
  loadRemote,
  loadLocalDraft,
  saveLocalDraft,
  clearLocalDraft,
  saveRemote,
  pingServer,
  downloadJson,
  refreshVideosRemote,
  deploySite,
} from './eventsStorage';

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function formatDate(str) {
  const parts = String(str || '').split('/');
  if (parts.length !== 3) return str || '—';
  const [m, d, y] = parts.map(Number);
  if (!m || !d || !y) return str;
  return `${d} ${MONTHS_ES[m - 1]} ${y}`;
}

function ymd(str) {
  const parts = String(str || '').split('/');
  if (parts.length !== 3) return '';
  const [m, d, y] = parts;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function isPast(dateStr) {
  const y = ymd(dateStr);
  if (!y) return false;
  return y < new Date().toISOString().slice(0, 10);
}

export default function AdminDashboard({ onLogout }) {
  const [collection, setCollection] = useState(emptyCollection());
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [serverUp, setServerUp] = useState(null); // null = unknown, true/false known
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'offline'
  const [editing, setEditing]   = useState(null);
  const [filter, setFilter]     = useState('upcoming');
  const [toast, setToast]       = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // index | null
  const [refreshingVideos, setRefreshingVideos] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployFile, setDeployFile] = useState(null);
  const [deployBusy, setDeployBusy] = useState(false);
  const [deployPct, setDeployPct]   = useState(0);
  const [dragOver, setDragOver]     = useState(false);
  const deployInputRef = useRef(null);

  const savedAtRef   = useRef(null); // Date — last successful server save
  const initialLoad  = useRef(true);

  // Initial load + server health
  useEffect(() => {
    let mounted = true;

    (async () => {
      const up = await pingServer();
      if (!mounted) return;
      setServerUp(up);

      try {
        // If server is up, always load authoritative data from it.
        // If server is down, hydrate from localStorage draft or static file.
        let data;
        if (up) {
          data = await loadRemote();
          clearLocalDraft(); // server is source of truth, drop stale drafts
        } else {
          const draft = loadLocalDraft();
          data = draft || await loadRemote();
        }
        if (!mounted) return;
        setCollection(data);
        setSyncStatus(up ? 'saved' : 'offline');
      } catch (err) {
        if (mounted) setLoadError(err.message || 'Error al cargar');
      } finally {
        if (mounted) {
          setLoading(false);
          initialLoad.current = false;
        }
      }
    })();

    return () => { mounted = false; };
  }, []);

  const showToast = useCallback((text, type = 'ok') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  // Persist changes — to server if up, else to localStorage
  const persist = useCallback(async (next) => {
    if (serverUp) {
      setSyncStatus('saving');
      try {
        await saveRemote(next);
        savedAtRef.current = new Date();
        setSyncStatus('saved');
        clearLocalDraft();
        return { ok: true, mode: 'server' };
      } catch (err) {
        // Server failed mid-session — fall back to draft
        setServerUp(false);
        saveLocalDraft(next);
        setSyncStatus('offline');
        showToast(`Error servidor: ${err.message}. Cambios guardados localmente.`, 'warn');
        return { ok: false, mode: 'local', error: err.message };
      }
    } else {
      saveLocalDraft(next);
      setSyncStatus('offline');
      return { ok: true, mode: 'local' };
    }
  }, [serverUp, showToast]);

  const openNew  = () => setEditing({ index: null, feature: emptyEvent() });
  const openEdit = (idx) => setEditing({ index: idx, feature: collection.features[idx] });
  const closeEdit = () => setEditing(null);

  const handleSave = async (feature) => {
    const next = {
      ...collection,
      features: editing.index == null
        ? [...collection.features, feature]
        : collection.features.map((f, i) => i === editing.index ? feature : f),
    };
    setCollection(next);
    const isNew = editing.index == null;
    setEditing(null);
    const res = await persist(next);
    if (res.ok) {
      showToast(
        res.mode === 'server'
          ? (isNew ? 'Evento agregado y publicado' : 'Evento actualizado y publicado')
          : (isNew ? 'Evento agregado (pendiente exportar)' : 'Evento actualizado (pendiente exportar)'),
        res.mode === 'server' ? 'ok' : 'warn'
      );
    }
  };

  /** Form delete button → closes the form and asks for confirmation. */
  const handleDelete = () => {
    if (editing == null || editing.index == null) { setEditing(null); return; }
    const idx = editing.index;
    setEditing(null);
    setConfirmingDelete(idx);
  };

  /** Row trash button → asks for confirmation (no edit form). */
  const requestDelete = (idx) => setConfirmingDelete(idx);

  /** Final delete after the confirmation modal. */
  const confirmDelete = async () => {
    const idx = confirmingDelete;
    if (idx == null || !collection.features[idx]) {
      setConfirmingDelete(null);
      return;
    }
    setConfirmingDelete(null);
    const next = {
      ...collection,
      features: collection.features.filter((_, i) => i !== idx),
    };
    setCollection(next);
    const res = await persist(next);
    if (res.ok) {
      showToast(
        res.mode === 'server' ? 'Evento eliminado' : 'Evento eliminado (pendiente exportar)',
        'warn'
      );
    }
  };

  const cancelDelete = () => setConfirmingDelete(null);

  const handleExport = () => {
    downloadJson(collection);
    showToast('eventos.json descargado');
  };

  const pickDeployFile = (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      showToast('El archivo debe ser un .zip del build', 'warn');
      return;
    }
    setDeployFile(file);
  };

  const closeDeploy = () => {
    if (deployBusy) return;
    setDeployOpen(false);
    setDeployFile(null);
    setDeployPct(0);
    setDragOver(false);
  };

  const runDeploy = async () => {
    if (!deployFile || deployBusy) return;
    setDeployBusy(true);
    setDeployPct(0);
    try {
      const res = await deploySite(deployFile, setDeployPct);
      setDeployBusy(false);
      setDeployOpen(false);
      setDeployFile(null);
      setDeployPct(0);
      showToast(
        `Sitio actualizado · ${res.deployed} archivo${res.deployed === 1 ? '' : 's'}` +
          (res.skipped ? ` · ${res.skipped} omitido${res.skipped === 1 ? '' : 's'}` : ''),
        'ok'
      );
    } catch (err) {
      setDeployBusy(false);
      showToast(`Error al actualizar el sitio: ${err.message}`, 'warn');
    }
  };

  const handleRefreshVideos = async () => {
    setRefreshingVideos(true);
    try {
      const res = await refreshVideosRemote();
      const n = res.added?.length || 0;
      showToast(
        n > 0
          ? `${n} video${n === 1 ? '' : 's'} nuevo${n === 1 ? '' : 's'} · ${res.count} en total`
          : `Sin videos nuevos · ${res.count} en total`,
        'ok'
      );
    } catch (err) {
      showToast(`Error al actualizar videos: ${err.message}`, 'warn');
    } finally {
      setRefreshingVideos(false);
    }
  };

  const handleReloadServer = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm('¿Recargar desde el servidor y descartar cambios locales?')) return;
    setLoading(true);
    try {
      const up = await pingServer();
      setServerUp(up);
      const data = await loadRemote();
      setCollection(data);
      clearLocalDraft();
      setSyncStatus(up ? 'saved' : 'offline');
      showToast('Recargado desde el servidor');
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySync = async () => {
    const up = await pingServer();
    setServerUp(up);
    if (!up) { showToast('Servidor sigue sin responder', 'warn'); return; }
    const res = await persist(collection);
    if (res.ok && res.mode === 'server') showToast('Cambios sincronizados con el servidor');
  };

  const visibleFeatures = useMemo(() => {
    const all = collection.features.map((f, i) => ({ f, i }));
    const filtered = filter === 'all'
      ? all
      : all.filter(({ f }) => filter === 'past' ? isPast(f.properties.date) : !isPast(f.properties.date));
    return filtered.sort((a, b) => ymd(a.f.properties.date).localeCompare(ymd(b.f.properties.date)));
  }, [collection, filter]);

  const counts = useMemo(() => {
    const all = collection.features;
    const up = all.filter(f => !isPast(f.properties.date)).length;
    return { all: all.length, upcoming: up, past: all.length - up };
  }, [collection]);

  if (loading) {
    return (
      <div className="ck-admin">
        <div className="ck-admin__loader">Cargando…</div>
      </div>
    );
  }

  if (loadError && !collection.features.length) {
    return (
      <div className="ck-admin">
        <div className="ck-admin__loader">
          Error: {loadError}
          <button onClick={() => window.location.reload()} className="ck-admin-btn ck-admin-btn--ghost" style={{ marginTop: 16 }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ck-admin">
      {/* Header */}
      <header className="ck-admin__header">
        <div className="ck-admin__header-brand">
          <img src={process.env.PUBLIC_URL + '/assets/nav-logo.png'} alt="C-Kan" />
          <div>
            <h1>Panel de eventos</h1>
            <p>Gestión del calendario de giras</p>
          </div>
        </div>

        <div className="ck-admin__header-actions">
          <div className={`ck-admin__sync ck-admin__sync--${syncStatus}`}>
            {syncStatus === 'saving'  && (<><i className="fa-solid fa-arrows-rotate fa-spin" /> Guardando…</>)}
            {syncStatus === 'saved'   && (<><i className="fa-solid fa-circle-check" /> Sincronizado</>)}
            {syncStatus === 'offline' && (<><i className="fa-solid fa-triangle-exclamation" /> Sin servidor</>)}
            {syncStatus === 'idle'    && (<>—</>)}
          </div>
          {serverUp && (
            <button
              onClick={() => setDeployOpen(true)}
              className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm"
              title="Actualizar el sitio subiendo un build .zip (no toca eventos ni videos)"
            >
              <i className="fa-solid fa-cloud-arrow-up" /> Actualizar sitio
            </button>
          )}
          <a href="/" className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
            <i className="fa-solid fa-arrow-up-right-from-square" /> Ver sitio
          </a>
          <button onClick={onLogout} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
            <i className="fa-solid fa-right-from-bracket" /> Salir
          </button>
        </div>
      </header>

      {/* Banner — server offline mode (localStorage + export workflow) */}
      {serverUp === false && (
        <div className="ck-admin__banner">
          <i className="fa-solid fa-circle-info" />
          <div>
            <strong>Servidor no disponible.</strong> Los cambios se guardan en tu navegador.
            Inicia el servidor con <code>node server.js</code> para sincronizar automáticamente,
            o exporta el JSON y reemplaza <code>public/assets/eventos.json</code> manualmente.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleRetrySync} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
              <i className="fa-solid fa-rotate" /> Reintentar
            </button>
            <button onClick={handleExport} className="ck-admin-btn ck-admin-btn--primary ck-admin-btn--sm">
              <i className="fa-solid fa-download" /> Exportar JSON
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="ck-admin__toolbar">
        <div className="ck-admin__filters" role="tablist">
          {[
            { id: 'upcoming', label: 'Próximos', count: counts.upcoming },
            { id: 'past',     label: 'Pasados',  count: counts.past },
            { id: 'all',      label: 'Todos',    count: counts.all },
          ].map(opt => (
            <button
              key={opt.id}
              role="tab"
              aria-selected={filter === opt.id}
              onClick={() => setFilter(opt.id)}
              className={`ck-admin-filter ${filter === opt.id ? 'is-active' : ''}`}
            >
              {opt.label} <span>{opt.count}</span>
            </button>
          ))}
        </div>

        <div className="ck-admin__toolbar-actions">
          {serverUp && (
            <button
              onClick={handleRefreshVideos}
              disabled={refreshingVideos}
              className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm"
              title="Buscar nuevos videos en el canal de YouTube"
            >
              <i className={`fa-brands fa-youtube ${refreshingVideos ? 'fa-fade' : ''}`} />
              {refreshingVideos ? ' Buscando…' : ' Actualizar videos'}
            </button>
          )}
          {serverUp && (
            <button onClick={handleReloadServer} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
              <i className="fa-solid fa-rotate-left" /> Recargar
            </button>
          )}
          <button onClick={handleExport} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
            <i className="fa-solid fa-download" /> Exportar JSON
          </button>
          <button onClick={openNew} className="ck-admin-btn ck-admin-btn--primary">
            <i className="fa-solid fa-plus" /> Nuevo evento
          </button>
        </div>
      </div>

      {/* Table */}
      {visibleFeatures.length === 0 ? (
        <div className="ck-admin__empty">
          <i className="fa-solid fa-calendar-xmark" />
          <p>No hay eventos en esta vista.</p>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--sm">
              Ver todos
            </button>
          )}
        </div>
      ) : (
        <div className="ck-admin__table-wrap">
          <table className="ck-admin__table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Venue</th>
                <th>Ciudad</th>
                <th>Estado</th>
                <th className="ck-admin__table-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleFeatures.map(({ f, i }) => {
                const p = f.properties;
                const past = isPast(p.date);
                return (
                  <tr key={i} onClick={() => openEdit(i)} className="ck-admin__row">
                    <td className="ck-admin__cell-date">{formatDate(p.date)}</td>
                    <td>
                      <div className="ck-admin__cell-venue">{p.title || '—'}</div>
                      {p.address && <div className="ck-admin__cell-addr">{p.address}</div>}
                    </td>
                    <td>{p.city || '—'}{p.country ? ` · ${p.country}` : ''}</td>
                    <td>
                      <span className={`ck-admin__badge ${past ? 'ck-admin__badge--past' : 'ck-admin__badge--up'}`}>
                        {past ? 'Pasado' : 'Próximo'}
                      </span>
                    </td>
                    <td className="ck-admin__table-col-actions" onClick={e => e.stopPropagation()}>
                      <div className="ck-admin__row-actions">
                        <button
                          onClick={() => openEdit(i)}
                          className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--icon"
                          aria-label={`Editar ${p.title}`}
                          title="Editar"
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button
                          onClick={() => requestDelete(i)}
                          className="ck-admin-btn ck-admin-btn--ghost ck-admin-btn--icon ck-admin-btn--icon-danger"
                          aria-label={`Eliminar ${p.title}`}
                          title="Eliminar"
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="ck-admin__modal" onClick={closeEdit}>
          <div className="ck-admin__modal-card" onClick={e => e.stopPropagation()}>
            <div className="ck-admin__modal-head">
              <h2>{editing.index == null ? 'Nuevo evento' : 'Editar evento'}</h2>
              <button onClick={closeEdit} className="ck-admin__modal-close" aria-label="Cerrar">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <EventForm
              initial={editing.feature}
              onSave={handleSave}
              onCancel={closeEdit}
              onDelete={editing.index != null ? handleDelete : null}
            />
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmingDelete != null && collection.features[confirmingDelete] && (() => {
        const target = collection.features[confirmingDelete].properties || {};
        return (
          <div className="ck-admin__modal ck-admin__modal--confirm" onClick={cancelDelete}>
            <div
              className="ck-admin__modal-card ck-admin__modal-card--sm"
              onClick={e => e.stopPropagation()}
              role="alertdialog"
              aria-labelledby="ck-admin-confirm-title"
            >
              <div className="ck-admin__confirm-icon">
                <i className="fa-solid fa-triangle-exclamation" />
              </div>
              <h2 id="ck-admin-confirm-title">¿Eliminar este evento?</h2>
              <div className="ck-admin__confirm-body">
                <strong>{target.title || '—'}</strong>
                <span>
                  {[formatDate(target.date), target.city, target.country].filter(Boolean).join(' · ')}
                </span>
              </div>
              <p className="ck-admin__confirm-warn">Esta acción no se puede deshacer.</p>
              <div className="ck-admin__confirm-actions">
                <button onClick={cancelDelete} className="ck-admin-btn ck-admin-btn--ghost" autoFocus>
                  Cancelar
                </button>
                <button onClick={confirmDelete} className="ck-admin-btn ck-admin-btn--danger">
                  <i className="fa-solid fa-trash" /> Eliminar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Deploy site modal — drag & drop a build .zip */}
      {deployOpen && (
        <div className="ck-admin__modal" onClick={closeDeploy}>
          <div className="ck-admin__modal-card ck-admin__modal-card--sm" onClick={e => e.stopPropagation()}>
            <div className="ck-admin__modal-head">
              <h2>Actualizar sitio</h2>
              <button onClick={closeDeploy} className="ck-admin__modal-close" aria-label="Cerrar" disabled={deployBusy}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div
              className={`ck-admin__dropzone ${dragOver ? 'is-dragover' : ''} ${deployFile ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); if (!deployBusy) setDragOver(true); }}
              onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
              onDrop={e => { e.preventDefault(); setDragOver(false); if (!deployBusy) pickDeployFile(e.dataTransfer.files?.[0]); }}
              onClick={() => !deployBusy && deployInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input
                ref={deployInputRef}
                type="file"
                accept=".zip,application/zip"
                style={{ display: 'none' }}
                onChange={e => pickDeployFile(e.target.files?.[0])}
              />
              <i className={`fa-solid ${deployFile ? 'fa-file-zipper' : 'fa-cloud-arrow-up'}`} />
              {deployFile ? (
                <p><strong>{deployFile.name}</strong><br />{(deployFile.size / 1048576).toFixed(1)} MB</p>
              ) : (
                <p>Arrastra aquí el <strong>build .zip</strong><br />o haz clic para elegir</p>
              )}
            </div>

            <p className="ck-admin__dropzone-note">
              <i className="fa-solid fa-shield-halved" /> No se tocan <code>api/</code> (eventos y videos)
              ni <code>uploads/</code>. Solo se actualizan los archivos del sitio.
            </p>

            {deployBusy && (
              <div className="ck-admin__progress" aria-label={`Subiendo ${deployPct}%`}>
                <div className="ck-admin__progress-bar" style={{ width: `${deployPct}%` }} />
                <span>{deployPct < 100 ? `Subiendo… ${deployPct}%` : 'Desplegando…'}</span>
              </div>
            )}

            <div className="ck-admin__confirm-actions">
              <button onClick={closeDeploy} className="ck-admin-btn ck-admin-btn--ghost" disabled={deployBusy}>
                Cancelar
              </button>
              <button onClick={runDeploy} className="ck-admin-btn ck-admin-btn--primary" disabled={!deployFile || deployBusy}>
                <i className={`fa-solid ${deployBusy ? 'fa-arrows-rotate fa-spin' : 'fa-cloud-arrow-up'}`} />
                {deployBusy ? ' Actualizando…' : ' Desplegar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`ck-admin__toast ck-admin__toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-check'}`} />
          {toast.text}
        </div>
      )}
    </div>
  );
}
